import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { injectJsonLd } from "@/utils/seo";

const faqs = [
  {
    question: "What is Ava and how does it work?",
    answer: "Ava is an AI companion designed for real conversation and emotional support. She uses advanced AI to understand context, remember your conversations, and respond with genuine empathy. Simply open the app and start talking — Ava adapts to your communication style over time.",
  },
  {
    question: "Is Ava free to use?",
    answer: "Yes! Ava offers a free plan with up to 10 chats per day. For unlimited conversations, priority responses, and deep relationship insights powered by AI, you can upgrade to our Monthly plan ($14.99/month) or Yearly plan ($99/year, saving over 45%).",
  },
  {
    question: "Is my data and conversation history private?",
    answer: "Absolutely. Your conversations with Ava are end-to-end encrypted and never sold, shared, or used for advertising. We take privacy seriously — your relationship with Ava is yours alone. You can delete your account and data at any time.",
  },
  {
    question: "Can Ava replace therapy or professional help?",
    answer: "No. Ava is a companion, not a therapist or medical professional. She provides emotional support and a safe space to talk, but she cannot diagnose conditions or provide clinical treatment. If you're in crisis, please contact a licensed professional or call 988 in the US.",
  },
  {
    question: "Does Ava remember past conversations?",
    answer: "Yes! One of Ava's standout features is her memory. She remembers what you've shared in previous conversations, follows up on topics you care about, and builds a genuine understanding of you over time. This creates a sense of continuity that makes every conversation feel more meaningful.",
  },
  {
    question: "What platforms is Ava available on?",
    answer: "Ava is available on both iOS and Android. You can download the app from the Apple App Store or Google Play Store. A web version is also in development for even more accessibility.",
  },
  {
    question: "How do I cancel my subscription?",
    answer: "You can cancel your subscription at any time through your Apple App Store or Google Play Store account settings. Your premium features will remain active until the end of your current billing period. There are no cancellation fees.",
  },
  {
    question: "Who built Ava and why?",
    answer: "Ava was built by Neuroava LLC, a team passionate about using AI to support mental wellness and human connection. We believe everyone deserves to feel heard — no matter the hour, no matter the struggle. Ava was created to be that always-available, judgment-free companion.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.answer,
        },
      })),
    };
    return injectJsonLd("avachat-faq-ld", data);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("faq-visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    const items = sectionRef.current?.querySelectorAll(".faq-animate");
    items?.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="faqs"
      className="py-14 md:py-20 bg-white"
    >
      <style>{`
        .faq-animate {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }
        .faq-animate.faq-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .faq-animate:nth-child(1) { transition-delay: 0s; }
        .faq-animate:nth-child(2) { transition-delay: 0.05s; }
        .faq-animate:nth-child(3) { transition-delay: 0.1s; }
        .faq-animate:nth-child(4) { transition-delay: 0.15s; }
        .faq-animate:nth-child(5) { transition-delay: 0.2s; }
        .faq-animate:nth-child(6) { transition-delay: 0.25s; }
        .faq-animate:nth-child(7) { transition-delay: 0.3s; }
        .faq-animate:nth-child(8) { transition-delay: 0.35s; }
      `}</style>

      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
            <i className="ri-questionnaire-line"></i>
            FAQ
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
            Questions?
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              We have answers.
            </span>
          </h2>
          <p className="text-[#5a4a8a] text-base max-w-lg mx-auto">
            Everything you need to know about Ava. Can&apos;t find what you&apos;re looking for? Reach out to us directly.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="faq-animate bg-white rounded-2xl border border-[#e8e0ff] overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer hover:bg-[#faf8ff] transition-colors"
              >
                <span className="font-semibold text-[#1a1040] text-sm md:text-base leading-snug">
                  {faq.question}
                </span>
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300"
                  style={{
                    background: openIndex === i ? "linear-gradient(135deg, #7C3AED, #A855F7)" : "#f5f0ff",
                  }}
                >
                  <i
                    className={`ri-${openIndex === i ? "subtract" : "add"}-line text-sm transition-transform duration-300`}
                    style={{ color: openIndex === i ? "#ffffff" : "#7C3AED" }}
                  ></i>
                </div>
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-out"
                style={{
                  maxHeight: openIndex === i ? "300px" : "0px",
                  opacity: openIndex === i ? 1 : 0,
                }}
              >
                <div className="px-5 pb-5 pt-0">
                  <p className="text-[#5a4a8a] text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-10">
          <p className="text-[#7a6aaa] text-sm mb-3">
            Still have questions? We&apos;re here to help.
          </p>
          <Link
            to="/contact"
            className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-all"
            style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
          >
            <i className="ri-mail-line"></i>
            Contact Support
          </Link>
          <p className="text-[#9a8aaa] text-xs mt-4">
            Or email us directly at{" "}
            <a href="mailto:info@neuroava.com" className="text-[#7C3AED] hover:underline cursor-pointer">info@neuroava.com</a>
          </p>
        </div>
      </div>
    </section>
  );
}